import { FC, useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Badge,
  Image,
  Spinner,
  NativeSelectRoot,
  NativeSelectField,
} from '@chakra-ui/react';
import { FiArrowLeft, FiZap, FiRefreshCw } from 'react-icons/fi';
import { useDogStore } from '@/stores/dogStore';
import { useAuthStore } from '@/stores/authStore';
import { useAIProposalStore } from '@/stores/aiProposalStore';
import { AppLayout } from '@/components/layout/AppLayout';
import { AIMatingProposalCard } from '@/components/ai/AIMatingProposalCard';
import { Dog } from '@/types/dog';
import { aiAnalysisHistoryService } from '@/services/firebase/aiAnalysisHistoryService';
import { AI_ANALYSIS_DAILY_LIMIT } from '@/services/ai/config';

/**
 * メス犬選択カードコンポーネント
 */
interface FemaleDogCardProps {
  dog: Dog;
  isSelected: boolean;
  onSelect: (dog: Dog) => void;
}

const FemaleDogCard: FC<FemaleDogCardProps> = ({ dog, isSelected, onSelect }) => {
  return (
    <Box
      borderRadius="lg"
      overflow="hidden"
      bg="white"
      shadow={isSelected ? 'lg' : 'sm'}
      border="2px solid"
      borderColor={isSelected ? 'pink.500' : 'transparent'}
      transition="all 0.2s"
      _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
      cursor="pointer"
      onClick={() => onSelect(dog)}
    >
      {/* サムネイル画像 */}
      <Box position="relative" w="100%" h="120px" bg="gray.100">
        {dog.mainPhotoUrl ? (
          <Image src={dog.mainPhotoUrl} alt={dog.name} w="100%" h="100%" objectFit="cover" />
        ) : (
          <Box
            w="100%"
            h="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="gray.200"
          >
            <Text color="gray.400" fontSize="2xl">
              🐕
            </Text>
          </Box>
        )}

        {/* 選択チェック */}
        {isSelected && (
          <Box
            position="absolute"
            top={2}
            right={2}
            bg="pink.500"
            color="white"
            borderRadius="full"
            w="24px"
            h="24px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="sm"
          >
            ✓
          </Box>
        )}
      </Box>

      {/* カード情報 */}
      <Box p={3}>
        <VStack align="stretch" gap={1}>
          <Text fontWeight="bold" fontSize="md" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
            {dog.name}
          </Text>
          <HStack gap={2}>
            <Badge colorPalette="pink" size="sm">
              メス
            </Badge>
            <Text fontSize="xs" color="gray.500">
              #{dog.bioNumber}
            </Text>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
};

/**
 * AI交配提案ページコンポーネント
 */
const AIMatingProposalPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { dogs, loading: dogsLoading, fetchDogs } = useDogStore();
  const {
    proposals,
    loading: proposalsLoading,
    error,
    disclaimer,
    fetchProposals,
    clearProposals,
  } = useAIProposalStore();

  const [selectedFemale, setSelectedFemale] = useState<Dog | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [remainingCount, setRemainingCount] = useState<number | null>(null);

  // 残り回数を取得
  const fetchRemainingCount = useCallback(async () => {
    if (!user) return;
    try {
      const todayCount = await aiAnalysisHistoryService.getTodayCount(user.uid);
      setRemainingCount(AI_ANALYSIS_DAILY_LIMIT - todayCount);
    } catch (err) {
      console.error('残り回数の取得に失敗:', err);
    }
  }, [user]);

  // URLパラメータからメス犬IDを取得
  const femaleIdFromUrl = searchParams.get('femaleId');

  // メス犬リストをフィルタリング
  const femaleDogs = useMemo(() => {
    return dogs.filter((dog) => dog.gender === 'female' && dog.status === 'active');
  }, [dogs]);

  // メス犬データの取得と残り回数の取得
  useEffect(() => {
    if (user) {
      fetchDogs(user.uid, 'female');
      fetchRemainingCount();
    }
  }, [user, fetchDogs, fetchRemainingCount]);

  // URLパラメータで指定されたメス犬を選択
  useEffect(() => {
    if (femaleIdFromUrl && femaleDogs.length > 0) {
      const female = femaleDogs.find((dog) => dog.id === femaleIdFromUrl);
      if (female) {
        setSelectedFemale(female);
      }
    }
  }, [femaleIdFromUrl, femaleDogs]);

  // メス犬選択時のハンドラ
  const handleSelectFemale = (dog: Dog) => {
    setSelectedFemale(dog);
    setHasSearched(false);
    clearProposals();
  };

  // AI分析実行
  const handleAnalyze = async () => {
    if (!selectedFemale || !user) return;

    setHasSearched(true);
    await fetchProposals(selectedFemale.id, user.uid);
    // 分析後に残り回数を更新
    fetchRemainingCount();
  };

  // 再分析
  const handleRefresh = async () => {
    if (!selectedFemale || !user) return;
    await fetchProposals(selectedFemale.id, user.uid);
    // 分析後に残り回数を更新
    fetchRemainingCount();
  };

  return (
    <AppLayout>
      <Box maxW="6xl" mx="auto">
        <VStack gap={6} align="stretch">
          {/* ヘッダー */}
          <HStack justify="space-between" flexWrap="wrap" gap={4}>
            <HStack gap={3}>
              <Button variant="ghost" onClick={() => navigate(-1)} size="sm">
                <FiArrowLeft />
              </Button>
              <Heading size="xl">
                <HStack gap={2}>
                  <FiZap />
                  <Text>AI交配提案</Text>
                </HStack>
              </Heading>
            </HStack>
            {/* 残り回数表示 */}
            {remainingCount !== null && (
              <Badge
                colorPalette={
                  remainingCount > 10 ? 'green' : remainingCount > 0 ? 'yellow' : 'red'
                }
                size="lg"
                px={3}
                py={1}
              >
                {remainingCount > 0
                  ? `AI分析: 本日あと${remainingCount}回`
                  : 'AI分析: 本日の上限に達しました'}
              </Badge>
            )}
          </HStack>

          {/* 説明文 */}
          <Box p={4} bg="blue.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="blue.500">
            <VStack align="start" gap={2}>
              <Text fontWeight="medium" color="blue.800">
                AIがメス犬に最適なオス犬の組み合わせを提案します
              </Text>
              <Text fontSize="sm" color="blue.600">
                遺伝的多様性、繁殖実績、特徴のマッチングを考慮して分析します。
                近親交配のリスクも自動で評価されます。
              </Text>
            </VStack>
          </Box>

          {/* メス犬選択セクション */}
          <Box bg="white" p={6} borderRadius="lg" shadow="md">
            <VStack align="stretch" gap={4}>
              <Heading size="md">1. メス犬を選択</Heading>

              {dogsLoading ? (
                <HStack justify="center" py={4}>
                  <Spinner size="md" />
                  <Text color="gray.500">読み込み中...</Text>
                </HStack>
              ) : femaleDogs.length === 0 ? (
                <Box p={6} textAlign="center" bg="gray.50" borderRadius="md">
                  <VStack gap={3}>
                    <Text color="gray.600">アクティブなメス犬が登録されていません</Text>
                    <Button colorPalette="blue" onClick={() => navigate('/dogs/new')}>
                      個体を登録する
                    </Button>
                  </VStack>
                </Box>
              ) : (
                <>
                  {/* モバイル向けセレクトボックス */}
                  <Box display={{ base: 'block', md: 'none' }}>
                    <NativeSelectRoot>
                      <NativeSelectField
                        value={selectedFemale?.id || ''}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const dog = femaleDogs.find((d) => d.id === e.target.value);
                          if (dog) handleSelectFemale(dog);
                        }}
                        placeholder="メス犬を選択してください"
                      >
                        {femaleDogs.map((dog) => (
                          <option key={dog.id} value={dog.id}>
                            {dog.name} (#{dog.bioNumber})
                          </option>
                        ))}
                      </NativeSelectField>
                    </NativeSelectRoot>
                  </Box>

                  {/* デスクトップ向けカードグリッド */}
                  <Box display={{ base: 'none', md: 'block' }}>
                    <SimpleGrid columns={{ md: 3, lg: 4 }} gap={4}>
                      {femaleDogs.map((dog) => (
                        <FemaleDogCard
                          key={dog.id}
                          dog={dog}
                          isSelected={selectedFemale?.id === dog.id}
                          onSelect={handleSelectFemale}
                        />
                      ))}
                    </SimpleGrid>
                  </Box>
                </>
              )}
            </VStack>
          </Box>

          {/* 分析ボタン */}
          {selectedFemale && (
            <Box bg="white" p={6} borderRadius="lg" shadow="md">
              <VStack gap={4}>
                <HStack gap={3}>
                  <Text fontWeight="medium">選択中:</Text>
                  <Badge colorPalette="pink" size="lg">
                    {selectedFemale.name}
                  </Badge>
                </HStack>

                <Button
                  colorPalette="blue"
                  size="lg"
                  onClick={handleAnalyze}
                  disabled={proposalsLoading || remainingCount === 0}
                  width={{ base: '100%', md: 'auto' }}
                >
                  <HStack gap={2}>
                    {proposalsLoading ? <Spinner size="sm" /> : <FiZap />}
                    <Text>
                      {proposalsLoading
                        ? 'AI分析中...'
                        : remainingCount === 0
                          ? '本日の上限に達しました'
                          : 'AI分析を実行'}
                    </Text>
                  </HStack>
                </Button>
                {remainingCount === 0 && (
                  <Text fontSize="sm" color="red.500">
                    本日のAI分析上限（{AI_ANALYSIS_DAILY_LIMIT}回）に達しました。明日以降にお試しください。
                  </Text>
                )}
              </VStack>
            </Box>
          )}

          {/* 分析結果セクション */}
          {hasSearched && (
            <Box bg="white" p={6} borderRadius="lg" shadow="md">
              <VStack align="stretch" gap={4}>
                <HStack justify="space-between">
                  <Heading size="md">2. 分析結果</Heading>
                  {!proposalsLoading && proposals.length > 0 && (
                    <Button variant="ghost" size="sm" onClick={handleRefresh}>
                      <HStack gap={1}>
                        <FiRefreshCw />
                        <Text>再分析</Text>
                      </HStack>
                    </Button>
                  )}
                </HStack>

                {/* ローディング */}
                {proposalsLoading && (
                  <VStack py={12} gap={4}>
                    <Spinner size="xl" color="blue.500" />
                    <Text color="gray.600">AI分析中...</Text>
                    <Text fontSize="sm" color="gray.500">
                      候補オス犬の評価を行っています。しばらくお待ちください。
                    </Text>
                  </VStack>
                )}

                {/* エラー */}
                {error && !proposalsLoading && (
                  <VStack py={8} gap={4}>
                    <Box
                      p={4}
                      bg="red.50"
                      borderRadius="md"
                      borderLeft="4px solid"
                      borderLeftColor="red.500"
                      width="100%"
                    >
                      <Text fontWeight="medium" color="red.700" mb={1}>
                        エラーが発生しました
                      </Text>
                      <Text fontSize="sm" color="red.600">
                        {error}
                      </Text>
                    </Box>
                    <Button onClick={handleRefresh} size="sm">
                      <HStack gap={1}>
                        <FiRefreshCw />
                        <Text>再試行</Text>
                      </HStack>
                    </Button>
                  </VStack>
                )}

                {/* 提案リスト */}
                {!proposalsLoading && !error && proposals.length > 0 && (
                  <VStack gap={4} align="stretch">
                    <Text fontSize="sm" color="gray.600">
                      {selectedFemale?.name}に最適なオス犬を{proposals.length}頭提案します
                    </Text>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
                      {proposals.map((proposal, index) => (
                        <AIMatingProposalCard
                          key={proposal.maleId}
                          proposal={proposal}
                          rank={index + 1}
                          femaleId={selectedFemale?.id || ''}
                          detailed
                        />
                      ))}
                    </SimpleGrid>
                  </VStack>
                )}

                {/* 候補なし */}
                {!proposalsLoading && !error && hasSearched && proposals.length === 0 && (
                  <VStack py={8} gap={4}>
                    <Text color="gray.600">提案可能なオス犬が見つかりませんでした</Text>
                    <Text fontSize="sm" color="gray.500">
                      アクティブなオス犬が登録されていることを確認してください
                    </Text>
                    <Button colorPalette="blue" variant="outline" onClick={() => navigate('/dogs/new')}>
                      オス犬を登録する
                    </Button>
                  </VStack>
                )}

                {/* 免責事項 */}
                {disclaimer && !proposalsLoading && proposals.length > 0 && (
                  <Box p={3} bg="gray.50" borderRadius="md">
                    <Text fontSize="xs" color="gray.500">
                      {disclaimer}
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>
          )}

          {/* ヒント */}
          {!hasSearched && !selectedFemale && (
            <Box p={6} bg="gray.50" borderRadius="lg" border="1px dashed" borderColor="gray.300">
              <VStack gap={3}>
                <Text fontSize="lg" color="gray.600">
                  メス犬を選択してAI分析を実行してください
                </Text>
                <Text fontSize="sm" color="gray.500">
                  AIが遺伝的多様性、繁殖実績、特徴のマッチングを考慮して最適なオス犬を提案します
                </Text>
              </VStack>
            </Box>
          )}
        </VStack>
      </Box>
    </AppLayout>
  );
};

export default AIMatingProposalPage;
